import { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { ChartBar, TrendUp, TrendDown, Package, Cube, ArrowsClockwise, CurrencyDollar, Star } from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const COLORS = ['#7d64ff', '#28cd8c', '#f6a609', '#ea4b5a', '#ff7d00', '#6153a7'];

function MetricCard({ icon: Icon, label, value, suffix, trend, trendType }) {
  return (
    <div className="bg-white border border-[#e5e5ea] rounded-lg p-5" data-testid={`metric-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">{label}</span>
        <Icon size={20} className="text-[#7d64ff]" />
      </div>
      <p className="text-2xl font-bold text-[#0A0A0A]">{value}{suffix}</p>
      {trend !== undefined && (
        <div className={`flex items-center gap-1 mt-1 text-xs font-medium ${trendType === 'good' ? 'text-[#28cd8c]' : 'text-[#ea4b5a]'}`}>
          {trendType === 'good' ? <TrendUp size={14} /> : <TrendDown size={14} />}
          {trend}
        </div>
      )}
    </div>
  );
}

export default function PerformancePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/performance/matrix`, { withCredentials: true })
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <DashboardLayout title="Seller Performance" subtitle="Track your marketplace performance metrics">
      <div className="space-y-6">
        <div className="h-32 skeleton rounded-xl" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{[1,2,3,4].map(i => <div key={i} className="h-28 skeleton rounded-xl" />)}</div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6"><div className="h-72 skeleton rounded-xl" /><div className="h-72 skeleton rounded-xl" /></div>
      </div>
    </DashboardLayout>
  );
  if (!data) return <DashboardLayout title="Performance"><p className="text-center text-[#5a5c87] py-12">Failed to load performance data</p></DashboardLayout>;

  const scoreColor = data.score >= 70 ? '#28cd8c' : data.score >= 40 ? '#f6a609' : '#ea4b5a';

  return (
    <DashboardLayout title="Seller Performance" subtitle="Track your marketplace performance metrics">
      {/* Score */}
      <div className="bg-white border border-[#e5e5ea] rounded-lg p-6 mb-6 flex items-center gap-8" data-testid="performance-score">
        <div className="relative w-28 h-28 flex-shrink-0">
          <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="42" fill="none" stroke="#f2f0ff" strokeWidth="8" />
            <circle cx="50" cy="50" r="42" fill="none" stroke={scoreColor} strokeWidth="8" strokeLinecap="round"
              strokeDasharray={`${data.score * 2.64} 264`} />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-2xl font-bold" style={{ color: scoreColor }}>{data.score}</span>
          </div>
        </div>
        <div>
          <h3 className="font-bold text-lg">Performance Score</h3>
          <p className="text-sm text-[#5a5c87] mt-1">
            {data.score >= 70 ? 'Great performance! Keep it up.' : data.score >= 40 ? 'Good, but there\'s room for improvement.' : 'Needs attention. Focus on fulfillment and reducing cancellations.'}
          </p>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <MetricCard icon={ChartBar} label="Fulfillment Rate" value={data.fulfillment_rate} suffix="%" trendType={data.fulfillment_rate >= 80 ? 'good' : 'bad'} trend={data.fulfillment_rate >= 80 ? 'On target' : 'Below target'} />
        <MetricCard icon={Package} label="Total Orders" value={data.total_orders} suffix="" />
        <MetricCard icon={CurrencyDollar} label="Total Revenue" value={`Rs.${data.total_revenue?.toLocaleString()}`} suffix="" />
        <MetricCard icon={Star} label="Avg Order Value" value={`Rs.${data.avg_order_value?.toLocaleString()}`} suffix="" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <MetricCard icon={TrendDown} label="Cancellation Rate" value={data.cancellation_rate} suffix="%" trendType={data.cancellation_rate <= 10 ? 'good' : 'bad'} trend={data.cancellation_rate <= 10 ? 'Healthy' : 'Too high'} />
        <MetricCard icon={ArrowsClockwise} label="Return Rate" value={data.return_rate} suffix="%" trendType={data.return_rate <= 5 ? 'good' : 'bad'} trend={data.return_rate <= 5 ? 'Healthy' : 'Above average'} />
        <MetricCard icon={Cube} label="Active Products" value={data.active_products} suffix={` / ${data.total_products}`} />
        <MetricCard icon={Package} label="Delivered" value={data.delivered_orders} suffix="" />
      </div>

      {/* Charts */}
      {data.category_breakdown?.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white border border-[#e5e5ea] rounded-lg p-6">
            <h3 className="font-bold text-base mb-4">Products by Category</h3>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data.category_breakdown}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="category" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="products" fill="#7d64ff" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-white border border-[#e5e5ea] rounded-lg p-6">
            <h3 className="font-bold text-base mb-4">Category Distribution</h3>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={data.category_breakdown} dataKey="products" nameKey="category" cx="50%" cy="50%" outerRadius={100} label={({ category, products }) => `${category}: ${products}`}>
                  {data.category_breakdown.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
